<template>

  <div class="ai-chat-container">
    <header class="fixed-header">
      <div class="header-left">
        <!-- 四角星图标 -->
        <svg class="header-icon" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2L15.09 8.91L22 12L15.09 15.09L12 22L8.91 15.09L2 12L8.91 8.91L12 2Z"/>
        </svg>
        <span class="header-title">AI 助手</span>
      </div>
      <div class="header-right">
        <el-link :underline="false" @click="clearChat" style="border-bottom:1px solid #f123;font-weight:600">
          <span style="font-size:12px;color:#f00;">清空内容</span>
        </el-link>
        <el-link :underline="false" @click="aiExit" style="border-bottom:1px solid #f123;font-weight:600">
          <span style="font-size:12px;color:#808695;">关闭</span>
        </el-link>
      </div>
    </header>

    <!-- 消息区 -->
    <div class="messages" ref="msgBox">
      <div
        v-for="(msg, idx) in messages"
        :key="idx"
        :class="['msg', msg.role]"
      >
        <div class="sender-tag">{{ msg.role === 'user' ? fromTitle : 'AI助手' }}</div>
        <div class="content" v-html="renderMarkdown(msg.content)"></div>
      </div>

      <!-- 连接建立前的占位，避免空白 -->
      <div v-if="loading && !hasReceivedFirstFrame" class="msg assistant">
        <div class="sender-tag">AI助手</div>
        <div class="content typing">正在连接中<span class="dot">...</span></div>
      </div>
    </div>

    <!-- 输入区 -->
    <div class="input-area">
      <textarea
        v-model="inputText"
        @keydown.enter.exact.prevent="sendMessage"
        placeholder="向AI助手提问..."
        :disabled="loading"
        class="ai-textarea"
      ></textarea>
      <span class="shortcut-tip">Enter 发送 · Shift+Enter 换行</span>
      <div class="btns">
        <button
          v-if="!loading"
          @click="sendMessage"
          :disabled="!inputText.trim()"
          class="send-btn"
        >
          <i class="iconfont icon-faqi"></i>发送
        </button>
        <button v-else @click="stopGeneration" class="stop-btn">停止</button>
      </div>
    </div>
  </div>
</template>

<script>
import { getDate, getDate2 } from '../api/Select'
import { marked } from 'marked'
marked.setOptions({
  gfm: true,
  breaks: true
})

// 无数据空闲超时：必须大于后端心跳间隔（15s），否则会误杀正常的长思考
const IDLE_TIMEOUT_MS = 25000

export default {
  name: 'AiChat',
  data() {
    return {
      fromTitle: '我',
      messages: [],
      inputText: '',
      loading: false,
      abortController: null,
      currentAnswer: '',
      hasReceivedFirstFrame: false
    }
  },
  beforeDestroy() {
    this.stopGeneration()
  },
  methods: {
    aiExit() {
      this.$store.commit('SET_SHOW_AI_CHAT', false)
    },

    clearChat() {
      if (this.abortController) {
        this.abortController.abort()
        this.abortController = null
      }
      this.messages = []
      this.currentAnswer = ''
      this.loading = false
      this.hasReceivedFirstFrame = false

      this.$nextTick(() => {
        const box = this.$refs.msgBox
        if (box) box.scrollTop = 0
      })

      this.resetSession()
    },

    resetSession() {
      const oldId = sessionStorage.getItem('mcp_session_id')
      fetch('/api/chat/session', {
        method: 'DELETE',
        headers: { 'X-Session-Id': oldId }
      }).catch(() => {})
      this.getSessionId()
    },

    getSessionId() {
      const telno = this.$cookies.get('v_telno') || 'guest'
      let sid = sessionStorage.getItem('mcp_session_id')
      if (!sid) {
        sid = telno + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8)
        sessionStorage.setItem('mcp_session_id', sid)
      }
      return sid
    },

    renderMarkdown(text) {
      if (!text) return ''
      try {
        return marked.parse(text)
      } catch (e) {
        return text.replace(/</g, '&lt;').replace(/>/g, '&gt;')
      }
    },

    async sendMessage() {
      const question = this.inputText.trim()
      if (!question || this.loading) return

      // 1. 先落用户气泡 + AI 占位气泡，界面 0 延迟响应
      this.messages.push({ role: 'user', content: question })
      this.inputText = ''
      this.currentAnswer = ''
      this.hasReceivedFirstFrame = false
      this.loading = true
      this.messages.push({ role: 'assistant', content: '' })

      // 用局部引用保存当前 AI 气泡，避免中途被清空后越界报错
      const aiMsg = this.messages[this.messages.length - 1]
      this.scrollToBottom()

      this.abortController = new AbortController()

      let reader = null
      let idleTimer = null
      let streamDone = false

      const clearIdleTimer = () => {
        if (idleTimer) {
          clearTimeout(idleTimer)
          idleTimer = null
        }
      }

      // 每收到任何数据就重置空闲计时器；长时间静默说明连接已失效
      const refreshIdleTimer = () => {
        clearIdleTimer()
        idleTimer = setTimeout(() => {
          streamDone = true
          if (reader) {
            try { reader.cancel('idle-timeout') } catch (e) { /* ignore */ }
          }
          if (this.abortController) {
            this.abortController.abort()
          }
          this.loading = false
        }, IDLE_TIMEOUT_MS)
      }

      try {
        const response = await fetch(
          '/api/chat/stream?question=' + encodeURIComponent(question),
          {
            method: 'GET',
            headers: {
              Accept: 'text/event-stream',
              'X-Session-Id': this.getSessionId()
            },
            signal: this.abortController.signal
          }
        )

        if (!response.ok) throw new Error('请求失败：HTTP ' + response.status)
        if (!response.body) throw new Error('当前浏览器不支持流式响应')

        reader = response.body.getReader()
        const decoder = new TextDecoder('utf-8')
        let buffer = ''
        refreshIdleTimer()

        for (;;) {
          if (streamDone) break

          const { done, value } = await reader.read()
          if (done) break

          refreshIdleTimer()
          if (value && value.length > 0) {
            buffer += decoder.decode(value, { stream: true })
          }

          const events = buffer.split(/\r?\n\r?\n/)
          buffer = events.pop() || ''

          for (let i = 0; i < events.length; i++) {
            const frame = this.parseFullSseFrame(events[i])
            if (!frame) continue

            this.hasReceivedFirstFrame = true

            // 心跳帧：只用来保活，不渲染
            if (frame.t === 'heartbeat') continue

            // 状态提示帧：覆盖当前 AI 气泡内容
            if (frame.t === 'status') {
              aiMsg.content = frame.v
              this.scrollToBottom()
              continue
            }

            // 结束帧：主动收敛，不等底层 close
            if (frame.t === 'done') {
              streamDone = true
              break
            }

            // 错误帧：写进气泡并立刻收敛
            if (frame.t === 'error') {
              this.currentAnswer += frame.v
              aiMsg.content = this.currentAnswer
              streamDone = true
              break
            }

            // 正文帧：逐字追加
            if (frame.t === 'content' && frame.v) {
              this.currentAnswer += frame.v
              aiMsg.content = this.currentAnswer
            }
          }

          this.$nextTick(() => this.scrollToBottom())
        }

        // 处理缓冲区里最后一段未被分隔符切开的数据
        if (buffer.trim()) {
          const frame = this.parseFullSseFrame(buffer)
          if (frame && frame.t === 'content' && frame.v) {
            this.currentAnswer += frame.v
            aiMsg.content = this.currentAnswer
          }
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          aiMsg.content = this.currentAnswer
            ? this.currentAnswer + '\n\n[客户端异常] ' + err.message
            : '出错了：' + err.message
        }
      } finally {
        // 三重兜底：无论正常结束 / 手动停止 / 异常 / 超时，状态一定归位
        clearIdleTimer()
        this.loading = false
        this.abortController = null
        this.hasReceivedFirstFrame = false
        this.$nextTick(() => this.scrollToBottom())
      }
    },

    // 解析 SSE 帧，兼容 [DONE] 结束标记
    parseFullSseFrame(rawEvent) {
      const lines = rawEvent.split(/\r?\n/)
      let payload = null
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i]
        if (line.slice(0, 5) === 'data:') {
          const raw = line.slice(5)
          if (raw.trim() === '[DONE]') {
            return { t: 'done', v: '' }
          }
          payload = payload === null ? raw.trim() : payload + '\n' + raw.trim()
        }
      }
      if (payload === null || payload === '') return null
      try {
        return JSON.parse(payload)
      } catch (e) {
        return null
      }
    },

    stopGeneration() {
      if (this.abortController) {
        this.abortController.abort()
        this.abortController = null
      }
      // 手动停止时立即释放按钮，不依赖流的回调时机
      this.loading = false
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const box = this.$refs.msgBox
        if (box) box.scrollTop = box.scrollHeight
      })
    }
  }
}
</script>

<style scoped>
.ai-chat-container {
  width: 100%;
  max-width: 800px;
  height: 99vh;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  overflow: hidden;
}

.fixed-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 8px;
  background: #fff;
  border-bottom: 1px solid #f0f2f5;
}
.header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.header-icon {
  width: 24px;
  height: 24px;
  color: #4080ff;
}
.header-title {
  font-size: 16px;
  font-weight: 600;
  color: #1d2129;
}
.header-right {
  display: flex;
  gap: 16px;
}

.messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  min-height: 0;
}

.msg {
  margin-bottom: 16px;
}
.msg.user {
  text-align: right;
}
.sender-tag {
  width: 65px;
  height: 24px;
  border-radius: 10px;
  background: #3370ff;
  color: #fff;
  display: flex;
  align-items: center;
  padding-left: 10px;
  flex-shrink: 0;
  font-size: 14px;
}
.msg.user .sender-tag {
  margin-left: auto;
  background: #929e8c;
}
.content {
  max-width: 100%;
  display: inline-block;
  margin-top: 1px;
  padding: 6px 16px;
  border-radius: 12px;
  background: #f5f5f5;
  line-height: 1.6;
  word-break: break-word;
  overflow-x: auto;
}
.msg.user .content {
  background: #e8f3ff;
}
.typing {
  color: #888;
}
.typing .dot {
  animation: blink 1s infinite;
}
@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}

.input-area {
  position: relative;
  border: 1px solid #c9d8f0;
  padding: 16px;
  flex-shrink: 0;
}
.ai-textarea {
  width: 100%;
  min-height: 100px;
  padding: 16px 40px 40px 16px;
  border-radius: 12px;
  font-size: 14px;
  color: #333;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.2s ease;
  resize: none;
}
.ai-textarea:focus {
  border-color: #4080ff;
  box-shadow: 0 0 0 3px rgba(64, 128, 255, 0.1);
}
.ai-textarea::placeholder {
  color: #a8b2c2;
}
.shortcut-tip {
  position: absolute;
  left: 16px;
  bottom: 6px;
  font-size: 10px;
  color: #9098a8;
  line-height: 1;
}
.btns {
  position: absolute;
  right: 12px;
  bottom: 12px;
  display: flex;
  flex-shrink: 0;
}
.send-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 12px;
  height: 32px;
  background-color: #69b1ff;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}
.send-btn:hover {
  background-color: #4080ff;
}
.send-btn:disabled {
  background-color: #c9d8f0;
  cursor: not-allowed;
}
.stop-btn {
  padding: 0 12px;
  height: 32px;
  background-color: #ff4d4f;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
}

/* Markdown 样式 */
::v-deep table {
  width: 100%;
  border-collapse: collapse;
  margin: 8px 0;
  font-size: 13px;
}
::v-deep th,
::v-deep td {
  border: 1px solid #ddd;
  padding: 6px 8px;
  text-align: left;
}
::v-deep th {
  background: #f0f0f0;
  font-weight: 600;
}
::v-deep pre {
  background: #2d2d2d;
  color: #f8f8f2;
  padding: 12px;
  border-radius: 6px;
  overflow-x: auto;
}
::v-deep code {
  font-family: Consolas, Monaco, monospace;
  font-size: 12px;
}
</style>
